import type * as Leaflet from 'leaflet';

export type OverlayLatLngTuple = [number, number];

export interface OverlayImageCorners {
  topLeft: OverlayLatLngTuple;
  topRight: OverlayLatLngTuple;
  bottomLeft: OverlayLatLngTuple;
}

export interface OverlayImageConfig {
  version: 1;
  imageId: string;
  opacity?: number;
  corners: OverlayImageCorners;
}

export const DEFAULT_OVERLAY_IMAGE_OPACITY = 0.65;
const LEGACY_DEFAULT_OVERLAY_IMAGE_OPACITY = 0.82;

export interface RotatedImageOverlay extends Leaflet.Layer {
  setCorners(topLeft: Leaflet.LatLng, topRight: Leaflet.LatLng, bottomLeft: Leaflet.LatLng): this;
  setOpacity(opacity: number): this;
}

export function parseOverlayImage(value: unknown): OverlayImageConfig | null {
  if (!value || typeof value !== 'string' || !value.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<OverlayImageConfig>;
    if (!parsed?.imageId || !parsed.corners) {
      return null;
    }

    const corners = normalizeOverlayCorners(parsed.corners);
    if (!corners) {
      return null;
    }

    return {
      version: 1,
      imageId: parsed.imageId,
      opacity: clampOpacity(parsed.opacity),
      corners
    };
  } catch {
    return null;
  }
}

export function serializeOverlayImage(config: OverlayImageConfig | null): string {
  return config ? JSON.stringify(config) : '';
}

export function overlayCornerToLatLng(leaflet: typeof Leaflet, value: OverlayLatLngTuple): Leaflet.LatLng {
  return leaflet.latLng(value[0], value[1]);
}

export function overlayCornersToLatLng(
  leaflet: typeof Leaflet,
  corners: OverlayImageCorners
): { topLeft: Leaflet.LatLng; topRight: Leaflet.LatLng; bottomLeft: Leaflet.LatLng } {
  return {
    topLeft: overlayCornerToLatLng(leaflet, corners.topLeft),
    topRight: overlayCornerToLatLng(leaflet, corners.topRight),
    bottomLeft: overlayCornerToLatLng(leaflet, corners.bottomLeft)
  };
}

export function latLngToOverlayTuple(latLng: Leaflet.LatLng): OverlayLatLngTuple {
  return [latLng.lat, latLng.lng];
}

export function createRotatedImageOverlay(
  leaflet: typeof Leaflet,
  imageUrl: string,
  corners: OverlayImageCorners,
  options: Leaflet.LayerOptions & { opacity?: number; className?: string; pane?: string; interactive?: boolean } = {}
): RotatedImageOverlay {
  const latLngCorners = overlayCornersToLatLng(leaflet, corners);

  const RotatedOverlay = (leaflet.Layer as any).extend({
    initialize(url: string, topLeft: Leaflet.LatLng, topRight: Leaflet.LatLng, bottomLeft: Leaflet.LatLng, layerOptions: any) {
      this._url = url;
      this._topLeft = topLeft;
      this._topRight = topRight;
      this._bottomLeft = bottomLeft;
      leaflet.setOptions(this, layerOptions);
    },

    onAdd(map: Leaflet.Map) {
      this._map = map;
      if (!this._image) {
        this._initImage();
      }

      const pane = this.options.pane ? map.getPane(this.options.pane) : map.getPanes().overlayPane;
      pane?.appendChild(this._image);
      map.on('zoomend viewreset moveend resize', this._reset, this);
      this._reset();
    },

    onRemove(map: Leaflet.Map) {
      if (this._image?.parentNode) {
        this._image.parentNode.removeChild(this._image);
      }
      map.off('zoomend viewreset moveend resize', this._reset, this);
    },

    setCorners(topLeft: Leaflet.LatLng, topRight: Leaflet.LatLng, bottomLeft: Leaflet.LatLng) {
      this._topLeft = topLeft;
      this._topRight = topRight;
      this._bottomLeft = bottomLeft;
      this._reset();
      return this;
    },

    setOpacity(opacity: number) {
      this.options.opacity = opacity;
      if (this._image) {
        this._image.style.opacity = `${opacity}`;
      }
      return this;
    },

    _initImage() {
      const className = ['leaflet-image-layer', this.options.className].filter(Boolean).join(' ');
      const image = leaflet.DomUtil.create('img', className);
      image.onload = () => this._reset();
      image.src = this._url;
      image.alt = '';
      image.draggable = false;
      image.style.border = 'none';
      image.style.display = 'block';
      image.style.maxHeight = 'none';
      image.style.maxWidth = 'none';
      image.style.position = 'absolute';
      image.style.width = '1px';
      image.style.height = '1px';
      image.style.transformOrigin = '0 0';
      image.style.opacity = `${this.options.opacity ?? 1}`;
      image.style.pointerEvents = this.options.interactive ? 'auto' : 'none';
      (image.style as any).userSelect = 'none';
      this._image = image;
    },

    _reset() {
      if (!this._map || !this._image) {
        return;
      }

      const topLeft = this._map.latLngToLayerPoint(this._topLeft);
      const topRight = this._map.latLngToLayerPoint(this._topRight);
      const bottomLeft = this._map.latLngToLayerPoint(this._bottomLeft);
      const imageWidth = this._image.naturalWidth || 1;
      const imageHeight = this._image.naturalHeight || 1;
      const widthVector = {
        x: topRight.x - topLeft.x,
        y: topRight.y - topLeft.y
      };
      const widthLength = Math.hypot(widthVector.x, widthVector.y);
      let heightVector = {
        x: bottomLeft.x - topLeft.x,
        y: bottomLeft.y - topLeft.y
      };

      if (widthLength > 0 && imageWidth > 0 && imageHeight > 0) {
        const expectedHeightLength = widthLength * imageHeight / imageWidth;
        const normal = { x: -widthVector.y / widthLength, y: widthVector.x / widthLength };
        const normalDirection = (heightVector.x * normal.x + heightVector.y * normal.y) < 0 ? -1 : 1;
        heightVector = {
          x: normal.x * expectedHeightLength * normalDirection,
          y: normal.y * expectedHeightLength * normalDirection
        };
      }

      const a = widthVector.x / imageWidth;
      const b = widthVector.y / imageWidth;
      const c = heightVector.x / imageHeight;
      const d = heightVector.y / imageHeight;

      this._image.style.width = `${imageWidth}px`;
      this._image.style.height = `${imageHeight}px`;
      this._image.style.transform = `matrix(${a}, ${b}, ${c}, ${d}, ${topLeft.x}, ${topLeft.y})`;
    }
  });

  return new RotatedOverlay(
    imageUrl,
    latLngCorners.topLeft,
    latLngCorners.topRight,
    latLngCorners.bottomLeft,
    options
  ) as RotatedImageOverlay;
}

function normalizeOverlayCorners(value: unknown): OverlayImageCorners | null {
  const corners = value as Partial<OverlayImageCorners>;
  if (!isValidTuple(corners?.topLeft) || !isValidTuple(corners?.topRight) || !isValidTuple(corners?.bottomLeft)) {
    return null;
  }

  return {
    topLeft: [corners.topLeft[0], corners.topLeft[1]],
    topRight: [corners.topRight[0], corners.topRight[1]],
    bottomLeft: [corners.bottomLeft[0], corners.bottomLeft[1]]
  };
}

function isValidTuple(value: unknown): value is OverlayLatLngTuple {
  return Array.isArray(value)
    && value.length === 2
    && Number.isFinite(Number(value[0]))
    && Number.isFinite(Number(value[1]));
}

function clampOpacity(value: unknown): number {
  const opacity = Number(value);
  if (!Number.isFinite(opacity)) {
    return DEFAULT_OVERLAY_IMAGE_OPACITY;
  }

  if (Math.abs(opacity - LEGACY_DEFAULT_OVERLAY_IMAGE_OPACITY) < 0.001) {
    return DEFAULT_OVERLAY_IMAGE_OPACITY;
  }

  return Math.min(1, Math.max(0, opacity));
}
